/*!
 * autosave.js - IndexedDB persistence for the Gang Sheet Builder.
 *
 * Stores the current project locally in the customer's browser so a closed
 * tab is not lost work. Two object stores:
 *   - 'meta'    key 'current': { v, at, sheetH, spacing, items: [snapshots] }
 *   - 'sources' key sourceId:  { name, type, isVector, blob }  (original file)
 *
 * The artwork blobs are written only once per source (they can be tens of
 * MB); the small meta record is rewritten on every change. Everything is
 * best-effort: on any error the promises resolve with null / false and the
 * app keeps working without persistence (e.g. private browsing).
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    root.Autosave = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DB_NAME = 'gang-sheet-builder';
  var DB_VERSION = 1;
  var dbPromise = null;

  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(function (resolve) {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      var req;
      try {
        req = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (e) { resolve(null); return; }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
        if (!db.objectStoreNames.contains('sources')) db.createObjectStore('sources');
      };
      req.onsuccess = function () {
        var db = req.result;
        // if another tab upgrades the schema, close so it can proceed
        db.onversionchange = function () { db.close(); dbPromise = null; };
        resolve(db);
      };
      req.onerror = function () { resolve(null); };
      req.onblocked = function () { resolve(null); };
    });
    return dbPromise;
  }

  // one read/write op wrapped as a promise that never rejects
  function op(storeNames, mode, fn) {
    return openDb().then(function (db) {
      if (!db) return null;
      return new Promise(function (resolve) {
        var tx;
        try {
          tx = db.transaction(storeNames, mode);
        } catch (e) { resolve(null); return; }
        var result = null;
        tx.oncomplete = function () { resolve(result); };
        tx.onerror = function () { resolve(null); };
        tx.onabort = function () { resolve(null); };
        try {
          result = fn(tx) || null;
        } catch (e) { resolve(null); }
      });
    });
  }

  return {
    // resolves true when the write went through (used to decide whether the
    // "you have unsaved work" beforeunload warning is still needed)
    saveMeta: function (meta) {
      return op(['meta'], 'readwrite', function (tx) {
        tx.objectStore('meta').put(meta, 'current');
        return true;
      });
    },

    putSource: function (id, rec) {
      return op(['sources'], 'readwrite', function (tx) {
        tx.objectStore('sources').put(rec, id);
        return true;
      });
    },

    deleteSource: function (id) {
      return op(['sources'], 'readwrite', function (tx) {
        tx.objectStore('sources').delete(id);
        return true;
      });
    },

    // -> { meta, sources: { id: rec } } or null when nothing usable is stored
    loadAll: function () {
      var out = { meta: null, sources: {} };
      return op(['meta', 'sources'], 'readonly', function (tx) {
        tx.objectStore('meta').get('current').onsuccess = function (e) {
          out.meta = e.target.result || null;
        };
        var cur = tx.objectStore('sources').openCursor();
        cur.onsuccess = function (e) {
          var c = e.target.result;
          if (c) { out.sources[c.key] = c.value; c.continue(); }
        };
        return out;
      }).then(function (res) {
        return res && res.meta && res.meta.items && res.meta.items.length ? res : null;
      });
    },

    clearAll: function () {
      return op(['meta', 'sources'], 'readwrite', function (tx) {
        tx.objectStore('meta').clear();
        tx.objectStore('sources').clear();
        return true;
      });
    }
  };
});
